import { io } from '../../vendor/socket.io-client.esm.min.js';
import {
  getCurrentUser,
  subscribeCurrentUser
} from '../user/current_user.js?v=20260722a';

let socket = null;
let activeUserId = null;
let unreadCount = 0;
let settingsVersion = '';
const listeners = new Set();

function emitLocal(type, detail = {}) {
  const event = { type, ...detail };
  listeners.forEach(listener => {
    try {
      listener(event);
    } catch (error) {
      console.warn('Message runtime listener failed', error);
    }
  });
}

function setUnreadCount(value) {
  const next = Math.max(0, Number(value) || 0);
  if (next === unreadCount) return;
  unreadCount = next;
  emitLocal('unread-count', { count: unreadCount });
}

async function readJsonResponse(response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload.ok === false) {
    const error = new Error(payload.error || 'Nachrichtenanfrage fehlgeschlagen.');
    error.code = payload.code || '';
    error.status = response.status;
    throw error;
  }
  return payload;
}

function rawFetch(path, options) {
  return fetch(path, {
    credentials: 'same-origin',
    ...options,
    headers: {
      ...(options.body ? { 'content-type': 'application/json' } : {}),
      ...(options.headers || {})
    }
  });
}

// One-time 401 retry: connectForUser() below fires syncUnreadCount() as a
// synchronous side effect of setCurrentUser() right after login, which can
// occasionally race the browser's Set-Cookie commit and 401 even though the
// session is valid (see api_utils.js's apiJson() for the full incident
// history — this is the same race, just for the one call site that doesn't
// go through apiJson). A short-delayed retry picks up the by-then-committed
// cookie; a genuinely invalid session still 401s on the retry.
async function fetchMessageJson(path, options = {}) {
  let response = await rawFetch(path, options);
  if (response.status === 401) {
    await new Promise(resolve => { setTimeout(resolve, 250); });
    response = await rawFetch(path, options);
  }
  return readJsonResponse(response);
}

async function syncUnreadCount() {
  if (!activeUserId) {
    setUnreadCount(0);
    return 0;
  }
  try {
    const payload = await fetchMessageJson('/api/messages/unread-count');
    if (activeUserId) setUnreadCount(payload.count);
  } catch (error) {
    emitLocal('error', { error });
  }
  return unreadCount;
}

function bindSocketEvents(target) {
  target.on('connect', () => {
    emitLocal('connection', { connected: true });
    syncUnreadCount();
    emitLocal('reconnect-sync');
  });
  target.on('disconnect', reason => emitLocal('connection', { connected: false, reason }));
  target.on('connect_error', error => emitLocal('connection-error', { error }));
  target.on('messages:unread-count', payload => setUnreadCount(payload?.count));
  target.on('messages:new', payload => emitLocal('new-message', payload || {}));
  target.on('messages:opened', payload => emitLocal('opened', payload || {}));
  target.on('messages:opened-self', payload => emitLocal('opened-self', payload || {}));
  target.on('messages:hidden', payload => emitLocal('hidden', payload || {}));
  target.on('messages:conversation-cleared', payload => emitLocal('conversation-cleared', payload || {}));
  target.on('messages:settings-updated', payload => {
    settingsVersion = String(payload?.updatedAt || Date.now());
    emitLocal('settings-updated', { ...payload, settingsVersion });
  });
}

function connectForUser(user) {
  const userId = Number(user?.benutzer_id || 0);
  if (!userId) {
    disconnectMessageRuntime();
    return;
  }
  if (activeUserId === userId && socket) {
    if (!socket.connected) socket.connect();
    return;
  }
  disconnectMessageRuntime({ preserveListeners: true });
  activeUserId = userId;
  socket = io({
    path: '/socket.io',
    autoConnect: false,
    withCredentials: true,
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 800,
    reconnectionDelayMax: 5000
  });
  bindSocketEvents(socket);
  socket.connect();
  syncUnreadCount();
}

function disconnectMessageRuntime(options = {}) {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
  activeUserId = null;
  unreadCount = 0;
  settingsVersion = '';
  if (!options.preserveListeners) emitLocal('reset', { count: 0 });
  else emitLocal('unread-count', { count: 0 });
}

function subscribeMessages(listener) {
  if (typeof listener !== 'function') return () => undefined;
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getMessageRuntimeState() {
  return {
    activeUserId,
    unreadCount,
    connected: socket?.connected === true,
    settingsVersion
  };
}

subscribeCurrentUser(user => connectForUser(user));
connectForUser(getCurrentUser());

export {
  disconnectMessageRuntime,
  fetchMessageJson,
  getMessageRuntimeState,
  subscribeMessages,
  syncUnreadCount
};
